import { useState } from 'react';

import { CheckoutSessionResponse, getCheckoutSession } from '../api/Subscription';
import { Price } from '../types/subscription';
import ensureError from '../utils/ensureError';
import { isOfflineOrNetworkFetchError } from '../utils/networkErrorUtils';

export const useSubscriptionCheckout = (returnUrl: string) => {
  const [checkoutSession, setCheckoutSession] = useState<CheckoutSessionResponse>();
  const [checkoutRequestPending, setCheckoutRequestPending] = useState(false);
  const [checkoutRequestError, setCheckoutRequestError] = useState<Error | undefined>();
  const [lastSelectedPrice, setLastSelectedPrice] = useState<Price | undefined>();

  const handlePriceSelect = (price: Price) => {
    setLastSelectedPrice(price);
    setCheckoutRequestError(undefined);

    // Early check for offline status before making the request
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setCheckoutRequestError(new Error('Network connection lost'));
      return;
    }

    setCheckoutRequestPending(true);
    void getCheckoutSession([price.price_id], returnUrl, true)
      .then(setCheckoutSession)
      .catch((e) => {
        if (process.env.NODE_ENV !== 'test') {
          console.error(e);
        }
        setCheckoutRequestError(ensureError(e));
      })
      .finally(() => setCheckoutRequestPending(false));
  };

  const clearCheckoutError = () => {
    setCheckoutRequestError(undefined);
  };

  const canRetryCheckout = !!lastSelectedPrice && !checkoutRequestPending;
  const checkoutIsNetworkIssue = !!checkoutRequestError && isOfflineOrNetworkFetchError(checkoutRequestError);

  const handleRetry = () => {
    if (lastSelectedPrice) {
      handlePriceSelect(lastSelectedPrice);
    }
  };

  return {
    checkoutSession,
    checkoutRequestPending,
    checkoutRequestError,
    handlePriceSelect,
    clearCheckoutError,
    canRetryCheckout,
    checkoutIsNetworkIssue,
    handleRetry,
  };
};

export default useSubscriptionCheckout;
